import React, { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../hooks/use-toast";
import { formatDate } from "../../lib/appUtils";
import { GlobalNav } from "../shared/GlobalNav";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { Badge } from "../ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { FileText, CheckCircle, Clock, Banknote, Eye, Signature } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ContractsPage = () => {
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [showContractModal, setShowContractModal] = useState(false);
  const [selectedContract, setSelectedContract] = useState(null);
  const [contractContent, setContractContent] = useState('');
  const { token, user, logout } = useAuth();
  const { toast } = useToast();

  const handleViewContract = async (contract) => {
    try {
      const response = await axios.get(`${API}/checkout/${contract.slug}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const content = response.data.contract.content;
      
      let displayContent = content;
      if (contract.contract_agreed && contract.contract_signature) {
        const signatureSection = `
          <div style="margin-top: 40px; padding: 20px; background-color: #f0f9ff; border-left: 4px solid #0ea5e9; border-radius: 4px;">
            <h3 style="color: #0ea5e9; margin-bottom: 10px;">✓ Contract Digitally Signed</h3>
            <p><strong>Signed by:</strong> ${contract.contract_signature}</p>
            <p><strong>Date:</strong> ${new Date(contract.contract_signed_at).toLocaleString()}</p>
            <p><strong>IP Address:</strong> ${contract.contract_signer_ip || 'N/A'}</p>
            <p style="font-size: 12px; color: #64748b; margin-top: 10px;">
              This contract has been digitally signed and is legally binding.
            </p>
          </div>
        `;
        displayContent += signatureSection;
      }
      
      setContractContent(displayContent);
      setSelectedContract(contract);
      setShowContractModal(true);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load contract content",
        variant: "destructive"
      });
    }
  };

  useEffect(() => {
    fetchContracts();
  }, []);

  const fetchContracts = async () => {
    try {
      const response = await axios.get(`${API}/contracts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setContracts(response.data || []);
    } catch (error) {
      console.error('Failed to fetch contracts:', error);
      toast({
        title: "Error",
        description: "Failed to fetch contracts",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const filteredContracts = contracts.filter(contract => {
    if (filter === 'signed') return contract.status === 'signed';
    if (filter === 'pending') return contract.status === 'pending';
    return true;
  });

  const getContractStats = () => {
    return {
      total: contracts.length,
      signed: contracts.filter(c => c.status === 'signed').length,
      pending: contracts.filter(c => c.status === 'pending').length,
      totalValue: contracts.reduce((sum, c) => sum + (c.total_amount || 0), 0)
    };
  };

  const stats = getContractStats();

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <GlobalNav user={user} onLogout={logout} currentPage="contracts" />
        <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#e6007e] mx-auto"></div>
            <p className="mt-4 text-gray-600 font-body">Loading contracts...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <GlobalNav user={user} onLogout={logout} currentPage="contracts" />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2 font-heading">Contracts</h1>
          <p className="text-gray-600 font-body">Manage all project contracts and signatures.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card className="border-0 shadow-sm">
            <CardContent className="p-6 text-center">
              <FileText className="w-8 h-8 text-green-600 mx-auto mb-2" />
              <p className="text-2xl font-bold text-gray-900 font-heading">{stats.total}</p>
              <p className="text-sm text-gray-600 font-body">Total Contracts</p>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm">
            <CardContent className="p-6 text-center">
              <CheckCircle className="w-8 h-8 text-green-600 mx-auto mb-2" />
              <p className="text-2xl font-bold text-gray-900 font-heading">{stats.signed}</p>
              <p className="text-sm text-gray-600 font-body">Signed</p>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm">
            <CardContent className="p-6 text-center">
              <Clock className="w-8 h-8 text-yellow-600 mx-auto mb-2" />
              <p className="text-2xl font-bold text-gray-900 font-heading">{stats.pending}</p>
              <p className="text-sm text-gray-600 font-body">Pending</p>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm">
            <CardContent className="p-6 text-center">
              <Banknote className="w-8 h-8 text-[#e6007e] mx-auto mb-2" />
              <p className="text-2xl font-bold text-gray-900 font-heading">£{stats.totalValue.toLocaleString()}</p>
              <p className="text-sm text-gray-600 font-body">Total Value</p>
            </CardContent>
          </Card>
        </div>

        <div className="flex items-center space-x-4 mb-6">
          <div className="flex rounded-md shadow-sm">
            <button
              onClick={() => setFilter('all')}
              className={`px-4 py-2 text-sm font-medium rounded-l-md border font-body ${
                filter === 'all' 
                  ? 'bg-green-600 text-white border-green-600' 
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
            >
              All Contracts
            </button>
            <button
              onClick={() => setFilter('signed')}
              className={`px-4 py-2 text-sm font-medium border-t border-b font-body ${
                filter === 'signed' 
                  ? 'bg-green-600 text-white border-green-600' 
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
            >
              Signed
            </button>
            <button
              onClick={() => setFilter('pending')}
              className={`px-4 py-2 text-sm font-medium rounded-r-md border font-body ${
                filter === 'pending' 
                  ? 'bg-green-600 text-white border-green-600' 
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
            >
              Pending Signature
            </button>
          </div>
        </div>

        <Card className="border-0 shadow-sm">
          <CardContent>
            {filteredContracts.length === 0 ? (
              <div className="text-center py-12">
                <Signature className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2 font-heading">
                  {filter === 'all' ? 'No contracts yet' : `No ${filter} contracts`}
                </h3>
                <p className="text-gray-600 font-body mb-6">
                  Contracts will appear here when clients proceed through the payment process.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredContracts.map((contract) => (
                  <div key={contract.id} className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-4 mb-2">
                          <h3 className="text-lg font-semibold text-gray-900 font-heading">{contract.title}</h3>
                          <Badge className={
                            contract.status === 'signed' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                          }>
                            {contract.status === 'signed' ? 'Signed' : 'Pending Signature'}
                          </Badge>
                        </div>
                        
                        <div className="flex items-center space-x-6 text-sm text-gray-600">
                          <span className="font-body">Client: {contract.client_name}</span>
                          <span className="font-body">Amount: £{contract.total_amount?.toLocaleString()}</span>
                          <span className="font-body">
                            {contract.status === 'signed' ? 
                              `Signed: ${formatDate(contract.signed_at)}` : 
                              `Created: ${formatDate(contract.created_at)}`
                            }
                          </span>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleViewContract(contract)}
                          className="font-body"
                          title="View Contract"
                        >
                          <Eye className="w-4 h-4 mr-2" />
                          View Contract
                        </Button>
                        
                        {contract.status === 'signed' && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => window.open(`${API}/contracts/${contract.id}/download`, '_blank')}
                            className="font-body"
                            title="Download Signed Contract"
                          >
                            <FileText className="w-4 h-4 mr-2" />
                            Download
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {showContractModal && selectedContract && (
        <Dialog open={showContractModal} onOpenChange={setShowContractModal}>
          <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading">
                {selectedContract.status === 'signed' ? '✓ Signed Contract' : 'Contract Preview'} - {selectedContract.title}
              </DialogTitle>
            </DialogHeader>
            <div className="bg-white rounded-lg border shadow-sm">
              <div 
                dangerouslySetInnerHTML={{ __html: contractContent }}
                className="contract-preview"
              />
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default ContractsPage;
